import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FACE_W, FACE_H, FACE_OUTLINE, REGION_SHAPES, REGION_TREATMENTS,
  REGION_NAMES, REGION_KEYS, mirrorOf,
} from './geometry/faceGeometry';
import styles from './demos.module.css';

/**
 * The injectables face map.
 *
 * Same RTL rule as the dental chart: the viewer's left is the PATIENT'S right,
 * which is why the region names themselves say so, and why the SVG is pinned
 * dir="ltr" and never flips.
 *
 * The mirror action is not a convenience — aesthetic treatment is usually
 * symmetric, so "treat the other side too" is how the product actually behaves.
 */
const FaceMapDemo = ({ color = '#8b5cf6', initialRegion = 'glabella' }) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(initialRegion);
  const mirror = mirrorOf(picked);

  return (
    <div className={styles.demoFrame} data-demo="injectables">
      <div className={styles.faceLayout}>
        <svg
          dir="ltr"
          className={styles.faceSvg}
          viewBox={`0 0 ${FACE_W} ${FACE_H}`}
          role="group"
          aria-label={t('demos.face.mapAria')}
        >
          <path d={FACE_OUTLINE.head} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
          <ellipse {...FACE_OUTLINE.eyeL} fill="#e2e8f0" />
          <ellipse {...FACE_OUTLINE.eyeR} fill="#e2e8f0" />

          {REGION_KEYS.map((key) => {
            const shape = REGION_SHAPES[key];
            const on = picked === key;
            const common = {
              className: styles.faceRegion,
              fill: on ? `${color}55` : `${color}12`,
              stroke: on ? color : `${color}55`,
              strokeWidth: on ? 1.6 : 0.8,
              role: 'button',
              tabIndex: 0,
              'aria-label': REGION_NAMES[key],
              'aria-pressed': on,
              onClick: () => setPicked(key),
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPicked(key); }
              },
            };
            return shape.kind === 'path'
              ? <path key={key} d={shape.d} {...common} />
              : <circle key={key} cx={shape.cx} cy={shape.cy} r={shape.r} {...common} />;
          })}
        </svg>

        <div className={styles.faceReadout} data-testid="face-readout">
          <strong className={styles.faceRegionName}>{REGION_NAMES[picked]}</strong>
          <ul className={styles.faceTreatments}>
            {(REGION_TREATMENTS[picked] || []).map((tx) => (
              <li key={tx} style={{ background: `${color}18`, color }}>{t(`demos.face.${tx}`, tx)}</li>
            ))}
          </ul>
          {mirror && (
            <button
              type="button"
              className={styles.faceMirrorBtn}
              data-testid="face-mirror"
              style={{ borderColor: color, color }}
              onClick={() => setPicked(mirror)}
            >
              {t('demos.face.mirror')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaceMapDemo;
